import type { ReactNode } from "react";
import { genres, regions, years } from "@/mocks/meta";

interface FilterPanelProps {
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: number[];
  open: boolean;
  onToggleOpen: () => void;
  onGenreChange: (value: string) => void;
  onRegionChange: (value: string) => void;
  onYearChange: (value: number) => void;
  onReset: () => void;
}

interface FilterGroupProps {
  title: string;
  children: ReactNode;
}

function FilterGroup({ title, children }: FilterGroupProps) {
  return (
    <div className="border-b border-background-200 py-4 last:border-b-0 last:pb-0">
      <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
        <span className="h-3.5 w-1 rounded-full bg-accent-500" />
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function Chip({ active, label, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "border-accent-500 bg-accent-500 text-primary-950"
          : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterPanel({
  selectedGenres,
  selectedRegions,
  selectedYears,
  open,
  onToggleOpen,
  onGenreChange,
  onRegionChange,
  onYearChange,
  onReset,
}: FilterPanelProps) {
  const hasActive =
    selectedGenres.length > 0 || selectedRegions.length > 0 || selectedYears.length > 0;

  return (
    <aside className="w-full lg:w-64 lg:shrink-0">
      <div className="rounded-lg border border-background-200 bg-background-50 lg:sticky lg:top-24">
        <div className="flex items-center justify-between p-4 lg:p-5">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
            <i className="ri-equalizer-line text-lg text-primary-600" />
            筛选条件
          </h2>
          <button
            type="button"
            onClick={onToggleOpen}
            className="flex items-center gap-1 rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-medium text-foreground-600 lg:hidden"
          >
            {open ? "收起" : "展开"}
            <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
        </div>

        <div className={`px-4 pb-4 lg:px-5 lg:pb-5 ${open ? "block" : "hidden lg:block"}`}>
          <FilterGroup title="类型">
            {genres.map((genre) => (
              <Chip
                key={genre}
                label={genre}
                active={selectedGenres.includes(genre)}
                onClick={() => onGenreChange(genre)}
              />
            ))}
          </FilterGroup>
          <FilterGroup title="地区">
            {regions.map((region) => (
              <Chip
                key={region}
                label={region}
                active={selectedRegions.includes(region)}
                onClick={() => onRegionChange(region)}
              />
            ))}
          </FilterGroup>
          <FilterGroup title="年份">
            {years.map((year) => (
              <Chip
                key={year}
                label={`${year}`}
                active={selectedYears.includes(year)}
                onClick={() => onYearChange(year)}
              />
            ))}
          </FilterGroup>

          <button
            type="button"
            onClick={onReset}
            disabled={!hasActive}
            className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-refresh-line text-base" />
            重置筛选
          </button>
        </div>
      </div>
    </aside>
  );
}